import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthProviderWrapper";
import axios from "axios";
import { BACKEND_URL } from "../../../config/config";
import { Button as ApasButton, ButtonLink } from "../../misc/button/Button";
import { Form, Input, Select, Checkbox, Spin, Alert, Button, Space } from "antd";
import InfoRow from "./InfoRow";
import { NotificationContext } from "../../context/NotificationWrapper";
import { renderCheckboxOrHidden } from "../../helper-function/helperFunction";
import DetacheGroup from "../multiAccount/DetacheGroup";
import RequestLaborModificationModal from "./RequestLaborModificationModal";
import AccountingFirmActivitySelect from "../../misc/AccountingFirmActivitySelect";
import {
  getNameRules,
  getFunctionRules,
  getPhoneRules,
  getEmailRules,
  getZipcodeRules,
  getRequiredTextRules,
} from "../../helper-function/validationRules";

const InfoDetailUpdate = () => {
  const { activeCompany, user, isAdmin, changeActiveCompany } =
    useContext(AuthContext);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { openNotification } = useContext(NotificationContext);
  const [conditionCheckbox, setConditionCheckbox] = useState(false);
  const userRef = useRef(user);
  const [laborToModify, setLaborToModify] = useState(null);
  const [requestLaborModificationModalVisible, setRequestLaborModificationModalVisible] = useState(false);

  // Données read-only (non gérées par le Form AntD)
  const [readOnlyData, setReadOnlyData] = useState({
    membershipCode: null,
    companyName: null,
    siren: null,
    laborUnion: null,
    workForce: null,
    buildingConventionOutside77: null,
  });

  const declarantType = Form.useWatch("declarantType", form);

  useEffect(() => {
    const condition = renderCheckboxOrHidden(
      { ...readOnlyData, ...form.getFieldsValue() },
      null
    );
    setConditionCheckbox(condition);
  }, [readOnlyData, form]);

  useEffect(() => {
    if (activeCompany && user) {
      setReadOnlyData({
        membershipCode: activeCompany.membershipCode,
        companyName: activeCompany.companyName,
        siren: activeCompany.siren,
        laborUnion: activeCompany.laborUnion,
        workForce: activeCompany.workForce,
        buildingConventionOutside77: activeCompany.buildingConventionOutside77,
      });

      form.setFieldsValue({
        // Company - Adresse
        address1: activeCompany.address1 || "",
        address2: activeCompany.address2 || "",
        address3: activeCompany.address3 || "",
        address4: activeCompany.address4 || "",
        zipcode: activeCompany.zipcode || "",
        city: activeCompany.city || "",

        // Company - Représentant légal
        representativeLastname: activeCompany.representativeLastname || "",
        representativeFirstname: activeCompany.representativeFirstname || "",
        representativeFunction: activeCompany.representativeFunction || "",
        representativePhone: activeCompany.representativePhone || "",
        representativeEmail: activeCompany.representativeEmail || "",

        // User - Déclarant
        declarantLastname: user.declarantLastname || "",
        declarantFirstname: user.declarantFirstname || "",
        declarantFunction: user.declarantFunction || "",
        declarantPhone: user.declarantPhone || "",
        declarantType: user.declarantType || "INTERNAL",

        // User - Cabinet comptable
        accountingFirmName: user.accountingFirmName || "",
        accountingFirmActivity: user.accountingFirmActivity || "",
        accountingFirmAddress: user.accountingFirmAddress || "",
        accountingFirmZipcode: user.accountingFirmZipcode || "",
        accountingFirmCity: user.accountingFirmCity || "",

        // Company - Référent APAS
        referentApasLastname: activeCompany.referentApasLastname || "",
        referentApasFirstname: activeCompany.referentApasFirstname || "",
        referentApasEmail: activeCompany.referentApasEmail || "",
      });
    }
  }, [activeCompany, user, form]);

  const handleRequestLaborModification = () => {
    setLaborToModify({
      laborUnion: readOnlyData.laborUnion,
      buildingConventionOutside77: readOnlyData.buildingConventionOutside77,
    });
    setRequestLaborModificationModalVisible(true);
  };

  const handleSubmit = (values) => {
    setIsLoading(true);
    const requestBody = {
      membershipCode: readOnlyData.membershipCode,

      // Company - Métier (read-only, envoyées depuis readOnlyData)
      laborUnion: readOnlyData.laborUnion,
      workForce: readOnlyData.workForce,
      buildingConventionOutside77: readOnlyData.buildingConventionOutside77,
      agreeOptionalWorker:
        readOnlyData.workForce === "moreThan10"
          ? readOnlyData.agreeOptionalWorker || false
          : false,

      // Company - Adresse
      address1: values.address1 || null,
      address2: values.address2,
      address3: values.address3 || null,
      address4: values.address4 || null,
      zipcode: values.zipcode,
      city: values.city,

      // Company - Représentant légal
      representativeFirstname: values.representativeFirstname || null,
      representativeFunction: values.representativeFunction || null,
      representativeLastname: values.representativeLastname || null,
      representativePhone: values.representativePhone || null,
      representativeEmail: values.representativeEmail || null,

      // Company - Référent APAS
      referentApasLastname: values.referentApasLastname,
      referentApasFirstname: values.referentApasFirstname,
      referentApasEmail: values.referentApasEmail,

      // User - Déclarant
      declarantLastname: values.declarantLastname,
      declarantFirstname: values.declarantFirstname,
      declarantFunction: values.declarantFunction,
      declarantPhone: values.declarantPhone,
      declarantType: values.declarantType,

      // User - Cabinet comptable (seulement si EXTERNAL)
      accountingFirmName:
        values.declarantType === "EXTERNAL" ? values.accountingFirmName : null,
      accountingFirmActivity:
        values.declarantType === "EXTERNAL"
          ? values.accountingFirmActivity
          : null,
      accountingFirmAddress:
        values.declarantType === "EXTERNAL"
          ? values.accountingFirmAddress
          : null,
      accountingFirmZipcode:
        values.declarantType === "EXTERNAL"
          ? values.accountingFirmZipcode
          : null,
      accountingFirmCity:
        values.declarantType === "EXTERNAL" ? values.accountingFirmCity : null,
    };

    axios
      .post(`${BACKEND_URL}/api/company/update`, requestBody, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then(() => {
        setIsLoading(false);
        navigate("/infoIdentifier", {
          state: { message: readOnlyData.membershipCode },
        });
      })
      .catch((error) => {
        setIsLoading(false);
        openNotification("Erreur", error.response.data.data, "error");
        console.error("error", error);
      });
  };

  const submitRequestModification = (companyInfo) => {
    setIsLoading(true);
    axios
      .post(
        `${BACKEND_URL}/api/company/requestChangeInfo`,
        {
          membershipCode: readOnlyData.membershipCode,
          laborUnionId: companyInfo.laborUnion,
          buildingConventionOutside77: companyInfo.buildingConventionOutside77,
          modificationReason: companyInfo.reason,
        },
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      )
      .then(() => {
        openNotification(
          "Succès",
          "Votre demande de modification a été envoyée",
          "success"
        );
        setRequestLaborModificationModalVisible(false);
        setLaborToModify(null);
      })
      .catch((error) => {
        openNotification(
          "Erreur",
          error.response?.data?.data || "Une erreur est survenue",
          "error"
        );
        console.error("error", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  userRef.current = user;

  const handleSuccessDetachGroup = () => {
    const currentUser = userRef.current;
    if (currentUser && currentUser?.companies?.length >= 1) {
      navigate("/infoIdentifier");
      changeActiveCompany({});
    } else if (currentUser && currentUser?.companies?.length === 0) {
      navigate("/empty");
    }
  };

  return (
    <>
      <Spin spinning={isLoading} fullscreen />
      <div className="info-detail update-page">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="new-password"
          data-testid="infoDetailUpdate"
        >
          <div className="title-container">
            <h2>Modification</h2>
            {!isAdmin && (
              <DetacheGroup
                page="infoDetail"
                data={activeCompany}
                afterClose={handleSuccessDetachGroup}
              />
            )}
          </div>

          <Alert
            message="Afin de modifier votre syndicat professionnel et la convention du batiment hors 77, veuillez envoyer une demande ici :"
            type="info"
            action={
              <Space direction="vertical">
                <Button
                  size="small"
                  type="primary"
                  onClick={handleRequestLaborModification}
                >
                  Demander la modification
                </Button>
              </Space>
            }
          />

          <div className="info-address" style={{ marginTop: 30 }}>
            <div className="info-identifiant">
              <InfoRow
                left="Identifiant APAS :"
                right={readOnlyData.membershipCode}
                width="150"
              />
              <InfoRow
                left="Raison sociale :"
                right={readOnlyData.companyName}
                width="150"
              />
              <InfoRow
                left="SIREN :"
                right={readOnlyData.siren}
                className="info-line"
              />
            </div>

            <div className="info-adherent-effectif">
              <div className="adherent">
                <InfoRow
                  left="Adhérent :"
                  right={
                    readOnlyData.laborUnion === 2
                      ? "CAPEB"
                      : readOnlyData.laborUnion === 3
                      ? "FFB"
                      : "aucun"
                  }
                />
              </div>
              <div className="effectif">
                <InfoRow
                  left="Effectifs :"
                  right={
                    readOnlyData.workForce === "moreThan10"
                      ? "Plus de 10 salariés"
                      : readOnlyData.workForce === "upTo10"
                      ? "Jusqu'à 10 salariés"
                      : "Pas de données"
                  }
                />
              </div>
              <div className="box-label-input">
                <Checkbox
                  disabled
                  checked={readOnlyData.buildingConventionOutside77}
                  className="checkbox"
                >
                  <span style={{ color: "black", fontWeight: "bold" }}>
                    Conventions du Bâtiment hors 77
                  </span>
                </Checkbox>
              </div>
            </div>

            <div>
              <Form.Item label="Complément d'adresse :" name="address1">
                <Input autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                label="Adresse – ligne 1 :"
                name="address2"
                rules={getRequiredTextRules("L'adresse – ligne 1")}
              >
                <Input autoComplete="new-password" />
              </Form.Item>

              <Form.Item label="Adresse – ligne 2 :" name="address3">
                <Input autoComplete="new-password" />
              </Form.Item>

              <Form.Item label="Votre référence (optionnel) :" name="address4">
                <Input autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                label="Code Postal :"
                name="zipcode"
                rules={getZipcodeRules(true)}
              >
                <Input autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                label="Ville :"
                name="city"
                rules={getRequiredTextRules("La ville")}
              >
                <Input autoComplete="new-password" />
              </Form.Item>
            </div>
          </div>

          <Alert
            message="Les informations du déclarant et du prestataire externe sont partagées entre toutes vos entreprises adhérentes."
            type="info"
            showIcon
            style={{ marginBottom: "20px" }}
          />

          <div className="info-declarant">
            <h3>Déclarant (comptable ou personne déléguée, interne ou externe)</h3>

            <Form.Item
              label="Nom :"
              name="declarantLastname"
              rules={getNameRules("Le nom")}
            >
              <Input autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label="Prénom :"
              name="declarantFirstname"
              rules={getNameRules("Le prénom")}
            >
              <Input autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label="Fonction :"
              name="declarantFunction"
              rules={getFunctionRules("La fonction")}
            >
              <Input autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label="Téléphone :"
              name="declarantPhone"
              rules={getPhoneRules(true)}
            >
              <Input autoComplete="new-password" />
            </Form.Item>

            <Form.Item label="Type de déclarant :" name="declarantType">
              <Select
                options={[
                  {
                    value: "INTERNAL",
                    label: "Interne (personne de l'entreprise)",
                  },
                  { value: "EXTERNAL", label: "Externe (prestataire externe)" },
                ]}
              />
            </Form.Item>
          </div>

          {declarantType === "EXTERNAL" && (
            <div className="info-cabinet">
              <h3>Prestataire externe</h3>

              <Form.Item
                label="Nom du prestataire (Précisez s'il s'agit d'un cabinet comptable) :"
                name="accountingFirmName"
                rules={getRequiredTextRules("Le nom du prestataire")}
              >
                <Input autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                label="Activité du prestataire externe :"
                name="accountingFirmActivity"
                rules={[
                  {
                    required: true,
                    message: "L'activité du prestataire est obligatoire",
                  },
                ]}
              >
                <AccountingFirmActivitySelect style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Adresse :"
                name="accountingFirmAddress"
                rules={getRequiredTextRules("L'adresse")}
              >
                <Input autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                label="Code postal :"
                name="accountingFirmZipcode"
                rules={getZipcodeRules(true)}
              >
                <Input autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                label="Ville :"
                name="accountingFirmCity"
                rules={getRequiredTextRules("La ville")}
              >
                <Input autoComplete="new-password" />
              </Form.Item>
            </div>
          )}

          <div className="info-referent">
            <h3>Représentant légal</h3>

            <Form.Item
              label="Nom :"
              name="representativeLastname"
              rules={[
                {
                  pattern: /^[a-zA-ZÀ-ÿ\s'-]+$/,
                  message:
                    "Le nom ne peut contenir que des lettres, espaces, traits d'union et apostrophes",
                },
              ]}
            >
              <Input autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label="Prénom :"
              name="representativeFirstname"
              rules={[
                {
                  pattern: /^[a-zA-ZÀ-ÿ\s'-]+$/,
                  message:
                    "Le prénom ne peut contenir que des lettres, espaces, traits d'union et apostrophes",
                },
              ]}
            >
              <Input autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label="Fonction :"
              name="representativeFunction"
              rules={[
                {
                  pattern: /^[a-zA-ZÀ-ÿ\s'-]+$/,
                  message:
                    "La fonction ne peut contenir que des lettres, espaces, traits d'union et apostrophes",
                },
              ]}
            >
              <Input autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label="Tél :"
              name="representativePhone"
              rules={getPhoneRules(false)}
            >
              <Input autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label="Email (optionnel) :"
              name="representativeEmail"
              rules={getEmailRules(false)}
            >
              <Input autoComplete="new-password" />
            </Form.Item>
          </div>

          <Alert
            message="Référent APAS :"
            description="Interlocuteur relais entre l'entreprise, les salariés et l'APAS-BTP.
              Rôle : recevoir les informations de l'association et les diffuser auprès des salariés, orienter les salariés vers l'association si besoin, faire remonter les questions ou besoins identifiés."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <div className="info-referent-apas">
            <h3>Référent APAS</h3>

            <Form.Item
              label="Nom :"
              name="referentApasLastname"
              rules={getNameRules("Le nom")}
            >
              <Input autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label="Prénom :"
              name="referentApasFirstname"
              rules={getNameRules("Le prénom")}
            >
              <Input autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label="Email :"
              name="referentApasEmail"
              rules={getEmailRules(true)}
            >
              <Input autoComplete="new-password" />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: "30px", marginTop: "20px" }}>
            <ButtonLink to="/infoIdentifier" text="Retour">
              Retour
            </ButtonLink>
            <ApasButton type="submit">
              Valider
            </ApasButton>
          </div>
        </Form>
      </div>

      <RequestLaborModificationModal
        visible={requestLaborModificationModalVisible}
        onCancel={() => setRequestLaborModificationModalVisible(false)}
        onConfirm={submitRequestModification}
        record={laborToModify}
      />
    </>
  );
};

export default InfoDetailUpdate;
